import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EmptyState } from '../../components/common/EmptyState';
import { SkeletonTable } from '../../components/common/SkeletonTable';
import { apiClient } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';

type TimeRangeValue = '1h' | '6h' | '24h' | '7d' | '30d';
type SortDirection = 'asc' | 'desc';
type EndpointSortField = 'endpoint' | 'method' | 'avgResponse' | 'p95' | 'errorRate' | 'requestCount';

interface SummaryShape {
  avgDurationMs?: number;
  p95DurationMs?: number;
  slowQueryPercentage?: number;
  totalQueries?: number;
  averageResponseTimeMs?: number;
  p95ResponseTimeMs?: number;
  errorRate?: number;
  totalRequests?: number;
}

interface EndpointRow {
  endpoint: string;
  method: string;
  avgResponse: number;
  p95: number;
  errorRate: number;
  requestCount: number;
}

const TIME_RANGE_OPTIONS: Array<{ value: TimeRangeValue; label: string; hours: number }> = [
  { value: '1h', label: '1h', hours: 1 },
  { value: '6h', label: '6h', hours: 6 },
  { value: '24h', label: '24h', hours: 24 },
  { value: '7d', label: '7d', hours: 24 * 7 },
  { value: '30d', label: '30d', hours: 24 * 30 },
];

function toMs(value: number): string {
  return `${Number.isFinite(value) ? value.toFixed(2) : '0.00'} ms`;
}

function toPercent(value: number): string {
  return `${Number.isFinite(value) ? value.toFixed(2) : '0.00'}%`;
}

function parseMethodAndEndpoint(requestPath: string): { method: string; endpoint: string } {
  const value = requestPath.trim();
  const match = /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\s+(.+)$/i.exec(value);
  if (!match) {
    return { method: '—', endpoint: value || '—' };
  }

  return { method: match[1].toUpperCase(), endpoint: match[2] || '—' };
}

function getHoursForRange(range: TimeRangeValue): number {
  return TIME_RANGE_OPTIONS.find((option) => option.value === range)?.hours ?? 24;
}

function formatTrendLabel(isoDate: string, hours: number): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;

  if (hours <= 24) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function PerformancePage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [sortField, setSortField] = useState<EndpointSortField>('avgResponse');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const hours = getHoursForRange(timeRange);

  const performanceQuery = useQuery({
    queryKey: ['admin-performance-dashboard', hours],
    queryFn: async () => {
      const [summary, endpoints, tables, trends, slowQueries] = await Promise.all([
        apiClient.getPerformanceSummary(hours),
        apiClient.getPerformanceByEndpoint(hours),
        apiClient.getPerformanceByTable(hours),
        apiClient.getPerformanceTrends(hours, 'hourly'),
        apiClient.getGroupedSlowQueries(hours, 20),
      ]);

      return { summary, endpoints, tables, trends, slowQueries };
    },
    refetchInterval: autoRefresh ? 30_000 : false,
  });

  const summary = performanceQuery.data?.summary as SummaryShape | undefined;

  const metrics = useMemo(
    () => ({
      avgResponseTime: summary?.avgDurationMs ?? summary?.averageResponseTimeMs ?? 0,
      p95Latency: summary?.p95DurationMs ?? summary?.p95ResponseTimeMs ?? 0,
      errorRate: summary?.slowQueryPercentage ?? summary?.errorRate ?? 0,
      totalRequests: summary?.totalQueries ?? summary?.totalRequests ?? 0,
    }),
    [summary],
  );

  const endpointRows = useMemo<EndpointRow[]>(() => {
    const rows = (performanceQuery.data?.endpoints ?? []).map((item) => {
      const { method, endpoint } = parseMethodAndEndpoint(item.requestPath ?? '');
      const requestCount = item.queryCount ?? item.requestCount ?? 0;
      const errorRate = item.errorRate ?? (requestCount > 0 ? (item.slowQueryCount / requestCount) * 100 : 0);
      return {
        endpoint,
        method: item.method ?? method,
        avgResponse: item.avgDurationMs ?? 0,
        p95: item.p95DurationMs ?? item.maxDurationMs ?? 0,
        errorRate,
        requestCount,
      };
    });

    const sorted = [...rows].sort((left, right) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      if (sortField === 'endpoint' || sortField === 'method') {
        return left[sortField].localeCompare(right[sortField]) * direction;
      }

      return (left[sortField] - right[sortField]) * direction;
    });

    return sorted;
  }, [performanceQuery.data?.endpoints, sortDirection, sortField]);

  const trendData = useMemo(
    () =>
      (performanceQuery.data?.trends ?? []).map((item) => ({
        label: formatTrendLabel(item.bucketStart, hours),
        avgDurationMs: item.avgDurationMs ?? 0,
        queryCount: item.queryCount ?? 0,
      })),
    [hours, performanceQuery.data?.trends],
  );

  const endpointChartData = useMemo(
    () =>
      [...(performanceQuery.data?.endpoints ?? [])]
        .sort((left, right) => (right.avgDurationMs ?? 0) - (left.avgDurationMs ?? 0))
        .slice(0, 15)
        .map((item) => ({
          endpointOrQuery: item.requestPath ?? '—',
          avgDurationMs: item.avgDurationMs ?? 0,
        })),
    [performanceQuery.data?.endpoints],
  );

  const tableChartData = useMemo(
    () =>
      [...(performanceQuery.data?.tables ?? [])]
        .sort((left, right) => (right.queryCount ?? 0) - (left.queryCount ?? 0))
        .slice(0, 15)
        .map((item) => ({
          tableName: item.tableOrContainer ?? '—',
          queryCount: item.queryCount ?? 0,
        })),
    [performanceQuery.data?.tables],
  );

  const hasData =
    metrics.totalRequests > 0 ||
    endpointRows.length > 0 ||
    endpointChartData.length > 0 ||
    tableChartData.length > 0 ||
    trendData.length > 0 ||
    (performanceQuery.data?.slowQueries.length ?? 0) > 0;

  const handleSort = (field: EndpointSortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('desc');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Performance</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Response trends, endpoint metrics, and slow query hotspots.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void performanceQuery.refetch()}
            disabled={performanceQuery.isFetching}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:hover:bg-gray-800"
          >
            {performanceQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
            />
            Auto-refresh
          </label>
        </div>
      </div>

      <section className="card">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Time range">
          {TIME_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTimeRange(option.value)}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition ${
                timeRange === option.value
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      {performanceQuery.isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="card animate-pulse">
                <div className="h-3 w-28 rounded bg-gray-300 dark:bg-gray-700" />
                <div className="mt-4 h-7 w-36 rounded bg-gray-300 dark:bg-gray-700" />
              </div>
            ))}
          </div>
          <div className="card">
            <SkeletonTable rows={6} columns={6} />
          </div>
        </div>
      ) : null}

      {performanceQuery.isError ? (
        <div className="card">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-300">Failed to load performance data</h2>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{performanceQuery.error?.message}</p>
          <button
            type="button"
            onClick={() => void performanceQuery.refetch()}
            className="mt-4 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            Try again
          </button>
        </div>
      ) : null}

      {!performanceQuery.isLoading && !performanceQuery.isError && !hasData ? (
        <EmptyState title="No performance data yet" message="No performance records were returned for the selected time range." />
      ) : null}

      {!performanceQuery.isLoading && !performanceQuery.isError && hasData ? (
        <>
          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg response time</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{toMs(metrics.avgResponseTime)}</p>
            </article>
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">P95 latency</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{toMs(metrics.p95Latency)}</p>
            </article>
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Error rate</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{toPercent(metrics.errorRate)}</p>
            </article>
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total requests</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {metrics.totalRequests.toLocaleString()}
              </p>
            </article>
          </section>

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Response time trends</h2>
            <div className="mt-4 h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" opacity={0.25} />
                  <XAxis dataKey="label" stroke="#9CA3AF" />
                  <YAxis yAxisId="left" stroke="#9CA3AF" />
                  <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="avgDurationMs" name="Avg Duration (ms)" stroke="#0EA5E9" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="queryCount" name="Total Query Count" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <article className="card">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Endpoint Performance (Top 15)</h2>
              <div className="mt-4 h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={endpointChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" opacity={0.25} />
                    <XAxis dataKey="endpointOrQuery" hide stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="avgDurationMs" name="Avg Duration (ms)" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>

            <article className="card">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Table Query Volume (Top 15)</h2>
              <div className="mt-4 h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tableChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" opacity={0.25} />
                    <XAxis dataKey="tableName" hide stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="queryCount" name="Query Count" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>
          </section>

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Endpoint performance</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-3 py-3 font-semibold">
                      <button type="button" className="hover:underline" onClick={() => handleSort('endpoint')}>Endpoint</button>
                    </th>
                    <th className="px-3 py-3 font-semibold">
                      <button type="button" className="hover:underline" onClick={() => handleSort('method')}>Method</button>
                    </th>
                    <th className="px-3 py-3 font-semibold">
                      <button type="button" className="hover:underline" onClick={() => handleSort('avgResponse')}>Avg response</button>
                    </th>
                    <th className="px-3 py-3 font-semibold">
                      <button type="button" className="hover:underline" onClick={() => handleSort('p95')}>P95</button>
                    </th>
                    <th className="px-3 py-3 font-semibold">
                      <button type="button" className="hover:underline" onClick={() => handleSort('errorRate')}>Error rate</button>
                    </th>
                    <th className="px-3 py-3 font-semibold">
                      <button type="button" className="hover:underline" onClick={() => handleSort('requestCount')}>Request count</button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {endpointRows.map((row) => (
                    <tr key={`${row.method}-${row.endpoint}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                      <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100">{row.endpoint}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{row.method}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{toMs(row.avgResponse)}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{toMs(row.p95)}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{toPercent(row.errorRate)}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{row.requestCount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {(performanceQuery.data?.slowQueries.length ?? 0) > 0 ? (
            <section className="card">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Slow queries</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-3 font-semibold">Endpoint / Query</th>
                      <th className="px-3 py-3 font-semibold">Table</th>
                      <th className="px-3 py-3 font-semibold">Operation</th>
                      <th className="px-3 py-3 font-semibold">Executions</th>
                      <th className="px-3 py-3 font-semibold">Avg duration</th>
                      <th className="px-3 py-3 font-semibold">Last seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {(performanceQuery.data?.slowQueries ?? []).map((item) => (
                      <tr key={`${item.endpointOrQuery}-${item.tableName}-${item.operation}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{item.endpointOrQuery}</td>
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{item.tableName || '—'}</td>
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{item.operation || '—'}</td>
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{item.executionCount.toLocaleString()}</td>
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{toMs(item.avgDurationMs)}</td>
                        <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{formatDateTime(item.lastSeen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
